import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import Countdown, { zeroPad } from 'react-countdown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

import config from '../../../config';
import cycleActions from '../../../redux/actions/cycleActions';
import { RootState } from '../../../redux/store';
import { CycleStatus } from '../../../types/CycleStatus';
import { TesterStatusInCycle } from '../../../types/TesterStatusInCycle';
import { UserStatus } from '../../../types/UserStatus';
import { WorkCategory } from '../../../types/WorkCategory';
import { CountChip, Notice, PageContainer, PageSection } from '../../../ui';
import { colors } from '../../../theme/tokens';

const workCategoryLabel = {
  [WorkCategory.FeedbackAndRating]: 'Get feedback and star rating',
  [WorkCategory.FindBugsExploratory]: 'Find bugs - exploratory',
  [WorkCategory.FindBugsTestPlan]: 'Find bugs - test plan',
};

const headCell = { fontSize: 11, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' as const, color: colors.textGrey, borderColor: colors.dividerBlue };

const viewPath = (cycle) => `/account/tester/cycle/${cycle.id}`;

/* View, or why the tester can't open the offer */
const ViewButton = ({ userStatus, cycle }) => {
  const view = (
    <Button variant="contained" size="small" component={RouterLink} to={viewPath(cycle)}>
      View
    </Button>
  );
  const bidClosed = (
    <Button variant="contained" size="small" disabled>
      Bid closed
    </Button>
  );

  if (cycle.id === config.evalCycleId) {
    return view;
  }

  const isRejected =
    cycle.testerStatus === TesterStatusInCycle.Rejected ||
    (typeof cycle.testerStatus === 'undefined' && cycle.status === CycleStatus.Testing);

  if (userStatus === UserStatus.Active) {
    return isRejected ? bidClosed : view;
  }

  if (userStatus === UserStatus.EvalCyclePending) {
    if (cycle.testerStatus === TesterStatusInCycle.Rejected) {
      return bidClosed;
    }
    return (
      <Tooltip title="You should complete the test cycle for new testers and get a rating from the Testpoint team first">
        <span>
          <Button variant="outlined" size="small" disabled>View</Button>
        </span>
      </Tooltip>
    );
  }

  return null;
};

const EndsIn = ({ date }) => (
  <Countdown
    date={date}
    daysInHours={false}
    renderer={({ days, hours, minutes, seconds }) => `${days} days, ${zeroPad(hours)}:${zeroPad(minutes)}:${zeroPad(seconds)}`}
  />
);

const UnreadMessages = ({ count }) =>
  count > 0 ? (
    <CountChip unread value={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><MailOutlineIcon sx={{ fontSize: 14 }} />{count}</Box>} title={`${count} unread message${count === 1 ? '' : 's'}`} />
  ) : null;

/* Testing offers / awaiting approval: title, time left, view */
const OfferCycles = ({ cycles, userStatus, isMobile }) =>
  isMobile ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {cycles.map((cycle) => (
        <Box key={cycle.id} sx={{ border: `1px solid ${colors.dividerBlue}`, borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{cycle.title}</Typography>
            <Typography sx={{ fontSize: 12, color: colors.textGrey }}>Ends in <EndsIn date={cycle.endDate} /></Typography>
          </Box>
          <ViewButton userStatus={userStatus} cycle={cycle} />
        </Box>
      ))}
    </Box>
  ) : (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={headCell}>Project title</TableCell>
          <TableCell sx={headCell}>Ends in</TableCell>
          <TableCell sx={headCell} />
        </TableRow>
      </TableHead>
      <TableBody>
        {cycles.map((cycle) => (
          <TableRow key={cycle.id} hover sx={{ '& td': { borderColor: '#f0f3f8' } }}>
            <TableCell sx={{ fontSize: 14 }}>{cycle.title}</TableCell>
            <TableCell sx={{ fontSize: 13, color: colors.textGrey, whiteSpace: 'nowrap' }}><EndsIn date={cycle.endDate} /></TableCell>
            <TableCell align="right"><ViewButton userStatus={userStatus} cycle={cycle} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

/* In progress / completed: title, last modified, work type, view + unread messages */
const WorkCycles = ({ cycles, isMobile }) => {
  const modified = (cycle) => (cycle.updatedAt ? moment(cycle.updatedAt).format('DD.MM.YYYY HH:mm') : '');
  const view = (cycle) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
      <UnreadMessages count={cycle.totalUnreadMessages} />
      <Button variant="contained" size="small" component={RouterLink} to={viewPath(cycle)} sx={{ ml: 1 }}>View</Button>
    </Box>
  );

  return isMobile ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {cycles.map((cycle) => (
        <Box key={cycle.id} sx={{ border: `1px solid ${colors.dividerBlue}`, borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{cycle.title}</Typography>
            <Typography sx={{ fontSize: 12, color: colors.textGrey }}>{workCategoryLabel[cycle.workCategory]}</Typography>
          </Box>
          {view(cycle)}
        </Box>
      ))}
    </Box>
  ) : (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={headCell}>Project title</TableCell>
          <TableCell sx={headCell}>Last modified</TableCell>
          <TableCell sx={headCell}>Work type</TableCell>
          <TableCell sx={headCell} />
        </TableRow>
      </TableHead>
      <TableBody>
        {cycles.map((cycle) => (
          <TableRow key={cycle.id} hover sx={{ '& td': { borderColor: '#f0f3f8' } }}>
            <TableCell sx={{ fontSize: 14 }}>{cycle.title}</TableCell>
            <TableCell sx={{ fontSize: 13, color: colors.textGrey, whiteSpace: 'nowrap' }}>{modified(cycle)}</TableCell>
            <TableCell sx={{ fontSize: 13, color: colors.textGrey }}>{workCategoryLabel[cycle.workCategory]}</TableCell>
            <TableCell>{view(cycle)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/* The four dashboard buckets. Eval-cycle testers keep their (only) cycle under In progress even after it completes. */
const sortCyclesByStatus = (cycles: any[], userStatus: UserStatus) => {
  const selecting = [CycleStatus.Selection, CycleStatus.TestingAndSelection];
  const working = [TesterStatusInCycle.Approved, TesterStatusInCycle.AwaitingReview, TesterStatusInCycle.Finished];
  const isEvalPending = userStatus === UserStatus.EvalCyclePending;

  const newCycles = cycles.filter((cycle) => typeof cycle.testerStatus === 'undefined' && selecting.includes(cycle.status));

  const rejectedCycles = cycles.filter(
    (cycle) =>
      // explicitly rejected, or never accepted and selection is over
      cycle.testerStatus === TesterStatusInCycle.Rejected || (typeof cycle.testerStatus === 'undefined' && cycle.status === CycleStatus.Testing)
  );

  const awaitingCycles = cycles.filter((cycle) => cycle.testerStatus === TesterStatusInCycle.Pending && selecting.includes(cycle.status));

  const inprogressCycles = cycles.filter(
    (cycle) =>
      working.includes(cycle.testerStatus) &&
      [...selecting, CycleStatus.Testing, ...(isEvalPending ? [CycleStatus.Completed] : [])].includes(cycle.status)
  );

  const completedCycles = cycles.filter((cycle) => working.includes(cycle.testerStatus) && !isEvalPending && cycle.status === CycleStatus.Completed);

  return {
    newAndRejected: [...newCycles, ...rejectedCycles],
    awaitingCycles,
    inprogressCycles,
    completedCycles: completedCycles.reverse(),
  };
};

/* Tester → Test cycles (account home) */
const AccountTester: React.FC = () => {
  const dispatch = useDispatch();
  const { user, cycles, resolution } = useSelector((state: RootState) => state);
  const isMobile = resolution.device === 'mobile' || resolution.device === 'mobile-tablet';
  const [chosenTab, setChosenTab] = useState(-1);

  useEffect(() => {
    dispatch(cycleActions.listCycles()); // TesterArea loads them too; this keeps the dashboard fresh on every visit
  }, []);

  const sorted = sortCyclesByStatus(cycles as any[], user.status);
  const buckets = [
    { label: 'Testing offers', cycles: sorted.newAndRejected, offers: true },
    { label: 'Awaiting approval', cycles: sorted.awaitingCycles, offers: true },
    { label: 'In progress', cycles: sorted.inprogressCycles, offers: false },
    { label: 'Completed', cycles: sorted.completedCycles, offers: false },
  ];

  // Until the tester picks a tab, open the first non-empty one
  const activeTab = chosenTab > -1 ? chosenTab : buckets.findIndex((bucket) => bucket.cycles.length > 0);

  const isEvalPending = user.status === UserStatus.EvalCyclePending;
  const showNewCycleIntro = isEvalPending && sorted.newAndRejected.length > 0 && sorted.inprogressCycles.length === 0 && sorted.completedCycles.length === 0;
  const onlyInProgress = sorted.inprogressCycles.length === 1 ? sorted.inprogressCycles[0] : null;
  const showAcceptedCycleIntro = isEvalPending && onlyInProgress?.testerStatus === TesterStatusInCycle.Approved;
  const showFinishedCycleIntro = isEvalPending && onlyInProgress?.testerStatus === TesterStatusInCycle.AwaitingReview;

  const active = buckets[activeTab];

  return (
    <PageContainer isMobile={isMobile}>
      {showNewCycleIntro && (
        <Notice title={`Welcome ${user.firstName}, you have completed your registration with Testpoint`}>
          Before we open up your account for paid testing cycles, you need to complete a quick evaluation test job, which the Testpoint team
          reviews to determine your initial Testpoint rating. Click &quot;View&quot; to continue.
        </Notice>
      )}
      {showAcceptedCycleIntro && (
        <Notice title="You have accepted your first evaluation cycle">
          Test cycles move through these tabs:
          <Box component="ul" sx={{ my: 1, pl: 2.5 }}>
            <li><b>Testing offers:</b> new test cycles that suit your profile</li>
            <li><b>Awaiting approval:</b> cycles you accepted that are pending approval before you can start working on them</li>
            <li><b>In progress:</b> cycles awarded to you that you can work on now</li>
            <li><b>Completed:</b> cycles you have completed</li>
          </Box>
          This mandatory cycle needs no approval, so it is already under <b>In progress</b>. Open that tab and click &quot;View&quot; to start.
        </Notice>
      )}
      {showFinishedCycleIntro && (
        <Notice title="All right! You've finished the mandatory cycle">
          We will review your report, determine your rating and send you a confirmation email. From then on you will get offers for paid
          testing cycles.
        </Notice>
      )}

      <PageSection padding={isMobile ? 1.5 : 2.5}>
        <Tabs
          value={activeTab > -1 ? activeTab : false}
          onChange={(_, value) => setChosenTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: `1px solid ${colors.dividerBlue}`, mb: 2 }}
        >
          {buckets.map((bucket) => (
            <Tab
              key={bucket.label}
              disabled={!bucket.cycles.length}
              sx={{ textTransform: 'none', minHeight: 44 }}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {bucket.label}
                  {bucket.cycles.length > 0 && <CountChip value={bucket.cycles.length} />}
                </Box>
              }
            />
          ))}
        </Tabs>

        {active ? (
          active.offers ? (
            <OfferCycles cycles={active.cycles} userStatus={user.status} isMobile={isMobile} />
          ) : (
            <WorkCycles cycles={active.cycles} isMobile={isMobile} />
          )
        ) : (
          <Typography variant="body2" color="textSecondary">
            No test cycles yet
          </Typography>
        )}
      </PageSection>
    </PageContainer>
  );
};

export default AccountTester;
